import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  // useContext,
} from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import useWizardStore, {
} from '@zs/useWizardStore';
import * as common from '@/common';
import * as elements from '@svg/elements';
import cache from '@/cache';
import {
  octSettings,
  octBoundsDeg,
} from '@/imageFiles';
import {
  LayoutRow,
  Coord,
  StimulusArray,
  getArrayColor,
  getStrokeColor,
  EditAction,
  TransformAction,
  getArrayStats,
  checkDistance,
} from '@t/LayoutTypes';
import VisualFieldCanvas from './VisualFieldCanvas';
import TransformGroup from './TransformGroup';
import { RetinalOverlay } from './RetinalOverlay';
import { LabelSlider } from './LabelSlider';
import { VesselMarker } from './VesselMarker';
import { Icon } from "@iconify/react/dist/iconify.js";
import { useImageCache } from '@/useImageCache';
import { useOctViewer } from '@svg/useOctViewer';
import BScroller from '@svg/BScroller';


interface BundleEditorProps {
  eye: 'OD' | 'OS';
  csvRows: LayoutRow[];
  bsc?: Coord;
  onComplete: (isComplete: boolean, edits?: EditAction[]) => void;
}
export const BundleEditor = ({
  eye,
  csvRows,
  bsc,
  onComplete,
}: BundleEditorProps) : JSX.Element => {
  const cacheLoaded = useWizardStore(state => state.cacheLoaded);
  const studyEye = useWizardStore(state => state.studyEye);
  const fovea = useWizardStore(state => state.fovealPit[eye]);
  const [blend, setBlend] = useState(0.8);
  const [step, setStep] = useState(0);
  const [didAction, setDidAction] = useState(false);
  const translateCoord = useRef<Coord>({ x: 0, y: 0 });
  const rotateDeg = useRef(0);

  const parentG = useRef<SVGGElement>(null);
  const myEdits = useRef<EditAction[]>([]);
  const [committedEdits, setCommittedEdits] = useState<string[]>([]);

  const {
    slice: { bSlice, setBSlice, handleBSlice },
    index: { bIndex, setBIndex, handleBScroll },
    enface: scanCoord,
  } = useOctViewer(eye);

  const markerArrays = useRef<StimulusArray[]>([]);
  const [markers, setMarkers] = useState<{id:string, c:Coord}[]>([]);
  const [markersDone, setMarkersDone] = useState(false);
  const setVascularCoords = useWizardStore(state => state.setVascularCoords);

  const animDiv = useRef<HTMLDivElement>(null);

  const steps = useMemo(() => {
    return getSteps(eye === studyEye);
  }, [eye]);

  const {
    targetRows,
  } = useMemo(() => {
    const lr = eye == 'OD' ? 'right' : 'left';
    const targetRows = new Map<string, LayoutRow[]>();
    for (const row of csvRows as LayoutRow[]) {
      if (!targetRows.has(row.array_id))
        targetRows.set(row.array_id, []);
      if (row.stimulus_eye == lr)
        targetRows.get(row.array_id)!.push(row);
    }
    return {
      targetRows,
    };
  }, [csvRows, eye]);

  const imgMemo = useMemo(() => {
    if (!cacheLoaded || cache.imageCount < 1)
      return undefined;
    // reg was done in raw pixel space, as unfixed->fixed. so layers are scaled together, as unfixed->local
    const imgCache = cache.images[eye]!;
    const overlayScale = fullDeg / imgCache.RED.crop.width;
    const fovDeg = elements.getFoveaDeg(fovea, imgCache.BSCAN.crop.width);
    return {
      cache: imgCache,
      overlayScale,
      fovDeg,
    };
  }, [eye, cacheLoaded, fovea]);

  useEffect(() => {
    const editedGroups = new Set(committedEdits.map(e => e.split(' ')[0]));
    const placed = editedGroups.size >= steps.filter(s => s.editType != 'mark').length;
    const done = placed && markersDone;
    if (!done)
      onComplete(false);
    else {
      setVascularCoords(eye, markers.map(m => m.c));
      onComplete(true, [...myEdits.current]);
    }
  }, [committedEdits, markers, markersDone, eye, csvRows, steps, onComplete]);

  const handleBlendSlider = useCallback((v: number) => {
    setBlend(v);
  }, []);

  const onTranslate = useCallback((id: string, v: Coord | number, m: DOMMatrixReadOnly) => {
    const currentStep = steps[step];
    let edit = myEdits.current.find(e => e.id == id);
    if (!edit) {
      edit = {
        id: id,
        arrayType: currentStep.arrayType,
        action: {
          translation: v as Coord,
          matrix: m,
        },
      };
      myEdits.current.push(edit);
    }
    else {
      edit.action.translation = v as Coord;
      edit.action.matrix = m;
    }
    setDidAction(true);
  }, [steps, step]);

  const onRotate = useCallback((id: string, v: Coord | number, m: DOMMatrixReadOnly) => {
    const currentStep = steps[step];
    let edit = myEdits.current.find(e => e.id == id);
    if (!edit) {
      edit = {
        id: id,
        arrayType: currentStep.arrayType,
        action: {
          rotation: v as number,
          matrix: m,
        },
      };
      myEdits.current.push(edit);
    }
    else {
      edit.action.rotation = v as number;
      edit.action.matrix = m;
    }
    setDidAction(true);
  }, [steps, step]);

  function onMark(id: string, marked: boolean, c: Coord) {
    if (marked)
      setMarkers([...markers, { id, c }]);
    else setMarkers(markers.filter(m => m.id != id));
  }

  const edited: JSX.Element[] = useMemo(() => {
    if (targetRows.size < 1) return [];
    const editedGroups = new Set<string>();
    const edited: JSX.Element[] = [];
    if (bsc) {
      const a = rowsToArray('BS-C', targetRows.get('BS-C')!);
      // const moved = { x: bsc.x - a.coords[0].x, y: bsc.y - a.coords[0].y };
      if (myEdits.current.length == 0 || myEdits.current[0].arrayType != 'BS-C') {
        const act = {
          translation: {...bsc},
          matrix: new DOMMatrixReadOnly([1, 0, 0, 1, bsc.x, bsc.y]),
        };
        myEdits.current.unshift({
          id: 'BS-C',
          arrayType: 'BS-C',
          action: act,
        });
      }
      edited.push(renderStaticArray(a, myEdits.current[0].action));
    }
    markerArrays.current.length = 0;
    for (let i = 0; i < steps.length; i++) {
      const currentStep = steps[i];
      if (currentStep.editType == 'mark')
        continue;
      if (!committedEdits.some(e => e.startsWith(currentStep.arrayType)))
        break;
      editedGroups.add(currentStep.arrayType);
      const stepEdits = myEdits.current.filter(e => e.arrayType == currentStep.arrayType);
      const rows = targetRows.get(currentStep.arrayType)!;
      const grp = makeGroup(rowsToArray(currentStep.arrayType, rows));
      for (const a of grp) {
        if (['BS', 'GA', 'SH', 'SV'].includes(a.id.substring(0, 2)))
          markerArrays.current.push(a);
        let edit = stepEdits.find(e => a.id == e.arrayType);
        if (a.id == 'PG')
          edit = stepEdits.find(e => e.id.substring(3) == a.members[0].toString());
        edited.push(renderStaticArray(a, edit ? edit.action : null));
      }
    }
    let add = 0;
    if (markersDone && steps.some(s => s.editType == 'mark'))
      add = 1;
    setStep(editedGroups.size + add);
    return edited;
  }, [committedEdits, markersDone, bsc, steps, eye, targetRows]);

  const editable: JSX.Element[] = useMemo(() => {
    const currentStep = steps[step];
    if (!imgMemo || !currentStep || currentStep.editType == 'mark')
      return [];
    const editable: JSX.Element[] = [];
    translateCoord.current = { x: 0, y: 0 };
    rotateDeg.current = 0;
    const rows = targetRows.get(currentStep.arrayType)!;
    const arrays = makeGroup(rowsToArray(currentStep.arrayType, rows));
    for (const a of arrays) {
      let tag = a.id;
      if (a.id == 'PG')
        tag += ` ${a.members[0]}`;
      switch (currentStep.editType) {
      case 'translate':
        const snapParams = {
          interval: scanIntervalDeg,
          offset: scanOffsetDeg - imgMemo!.fovDeg.y - a.coords[0].y,
        };
        editable.push(
          <TransformGroup key={tag} id={tag} mode={currentStep.editType} snap={snapParams} onUse={onTranslate}>
            {renderStaticArray(a, null)}
          </TransformGroup>
        );
        break;
      case 'rotate':
        editable.push(
          <TransformGroup key={tag} id={tag} mode={currentStep.editType} onUse={onRotate}>
            {renderStaticArray(a, null)}
          </TransformGroup>
        );
        break;
      }
    }
    return editable;
  }, [steps, imgMemo, step, targetRows, onTranslate, onRotate]);

  const currentStep = steps[step];
  let helpText = 'You have completed the layout for this eye! You may click Next at the bottom to proceed.';
  let helpDom = (<>{helpText}</>);
  let actionText = '';
  if (currentStep) {
    helpDom = (<>{currentStep.helpText}</>);
    if (currentStep.editType == 'translate')
      actionText = 'Click and drag the target item to move it.';
    else if (currentStep.editType == 'rotate')
      actionText = 'Click and drag the target item to rotate it around the grid origin.';
    else if (currentStep.editType == 'mark')
      actionText = 'Click any stimulus location to toggle markings.';
    if (['T', 'PG'].includes(currentStep.arrayType)) //haxx
      helpDom = (<>Move {currentStep.arrayType} stimuli away from <b>scotomas</b> or major <b>blood vessels</b>.</>);
  }

  if (targetRows.size < 1 || !imgMemo)
    return <div>Loading...</div>;

  function handleOKClick(e) {
    if (step < 0 || step >= steps.length)
      return;
    if (currentStep.editType == 'mark') {
      setMarkersDone(true);
      setDidAction(false);
      animDiv.current!.className = 'animate-[flash-in_0.5s]';
      setTimeout(() => {
        animDiv.current!.className = '';
      }, 500);
      return;
    }
    let batch = myEdits.current.filter(e => e.arrayType == currentStep.arrayType);
    if (batch.length == 0) {
      const mx = new DOMMatrixReadOnly();
      const edit = {
        id: currentStep.arrayType,
        arrayType: currentStep.arrayType,
        action: {
          translation: { x: 0, y: 0 },
          matrix: mx,
          pmx: mx,
        },
      };
      myEdits.current.push(edit);
      batch = [edit];
    }
    setCommittedEdits([...committedEdits, ...batch.map(e => e.id)]);
    if (!steps.some(s => s.editType == 'mark'))
      setMarkersDone(true);
    setDidAction(false);
    animDiv.current!.className = 'animate-[flash-in_0.5s]';
    setTimeout(() => {
      animDiv.current!.className = '';
    }, 500);
  }

  function handleUndoClick(e) {
    if (step < 1)
      return;
    const rewindTypes: string[] = [];
    const prevStep = steps[step-1];
    if (currentStep?.editType == 'mark' || prevStep.editType == 'mark') {
      setMarkers([]);
      setMarkersDone(false);
    }
    if (currentStep && currentStep.editType != 'mark') rewindTypes.push(currentStep.arrayType);
    if (prevStep.editType != 'mark') rewindTypes.push(prevStep.arrayType);
    myEdits.current = myEdits.current.filter(e => !rewindTypes.includes(e.arrayType));
    setCommittedEdits(myEdits.current.map(e => e.id));
    setDidAction(false);
    animDiv.current!.className = 'animate-[flash-in_0.5s]';
    setTimeout(() => {
      animDiv.current!.className = '';
    }, 500);
  }

  const staticMarkers: JSX.Element[] = [];
  const clickableMarkers: JSX.Element[] = [];
  if (currentStep && currentStep.editType == 'mark') {
    for (const a of markerArrays.current) {
      const rad = a.stimDiameter / 2;
      const e = myEdits.current.find(e => e.id == a.id);
      for (let i = 0; i < a.coords.length; i++) {
        const c = {...a.coords[i]};
        if (e) {
          const p = e.action.matrix.transformPoint(c);
          c.x = p.x;
          c.y = p.y;
        }
        let marked = false;
        for (const m of markers) {
          marked = checkDistance(m.c, c, rad);
          if (marked) break;
        }
        clickableMarkers.push(<VesselMarker key={a.members[i]} initValue={marked} id={a.members[i]} x={c.x} y={c.y} r={rad} onToggle={onMark} />);
      }
    }
  }
  else {
    for (const m of markers) {
      const r = markerArrays.current[0].stimDiameter / 2;
      staticMarkers.push(<line key={`x1 ${m.id}`} x1={m.c.x-r} y1={m.c.y-r} x2={m.c.x+r} y2={m.c.y+r} />);
      staticMarkers.push(<line key={`x2 ${m.id}`} x1={m.c.x+r} y1={m.c.y-r} x2={m.c.x-r} y2={m.c.y+r} />);
    }
  }

  const bCrop = imgMemo.cache.BSCAN.crop;
  const bscanCt = imgMemo.cache.BSCAN.urls.length;


  return (
    <div className='flex gap-2'>
      <div className='flex flex-col gap-1'>
        <VisualFieldCanvas width={600} height={600} pixelsPerDegree={10}>
          <g ref={parentG} transform='scale(10)'>
            <RetinalOverlay eye={eye} scanIndex={bIndex} bscanX={bSlice} blend={blend} />
            {edited}
            {editable}
            <g key='markers-interact' fill='transparent' stroke='white' strokeWidth={0.05}>
              {clickableMarkers}
              <g key='markers-static' style={{pointerEvents:'none'}}>
                {staticMarkers}
              </g>
            </g>
          </g>
        </VisualFieldCanvas>
        <LabelSlider id='blend' label='FAF Blend' initValue={80} onValue={handleBlendSlider} />
      </div>
      <div className='flex flex-col flex-1 gap-1'>
        <div className={`flex flex-row gap-1`}>
          <div className='flex flex-col gap-1'>
            <div className='text-xs'>Scan No.</div>
            <input
              type='number'
              value={bIndex + 1}
              onChange={handleBScroll}
              step={1}
              min={1}
              max={bscanCt}
              className='w-12 border rounded-md'
            />
            <input
              type='range'
              value={bIndex + 1}
              onChange={handleBScroll}
              step={1}
              min={1}
              max={bscanCt}
              style={{ writingMode: 'vertical-rl', direction: 'rtl' }}
              className='grow'
            />
          </div>
          <svg
            className='h-[300px]'
            viewBox={`${bCrop.x} ${bCrop.y} ${bCrop.width} ${bCrop.height}`}
          >
            <BScroller eye={eye} index={bIndex} onSlice={handleBSlice} />
          </svg>
        </div>
        <div className='text-sm'>Click and drag on the the B-scan above to place a corresponding vertical line on the view to the left.</div>
        <div ref={animDiv}>
          <article className='my-8'>
            <p className='text-wrap'>{step+1}. {helpDom}</p>
            {actionText && <p className='text-sm italic'>{actionText}</p>}
          </article>
          <div className='flex flex-row gap-1'>
            <button onClick={handleOKClick} disabled={step >= steps.length || (!didAction && currentStep.editType != 'mark') } className="btn">{currentStep?.editType == 'mark' ? 'Confirm Markings' : ' Confirm Placement'}</button>
            <button onClick={handleUndoClick} disabled={step < 1} className="btn bg-teal-600">Undo</button>
          </div>
          <div className="flex flex-col gap-1 p-2 border border-gray-300 text-gray-500 w-fit rounded-md mt-[20px]">
            <div className="flex items-center gap-x-[5px] font-semibold text-sm">
              How to use Layout Editor{" "}
              <span className="font-normal">(left view)</span>
              <Icon icon="heroicons:question-mark-circle" className="w-5 h-5" />
            </div>
            <div className="flex flex-col text-xs">
              <div className="font-semibold">Panning</div>
              <div className="flex gap-1">
                To pan the view, <strong>click</strong> and{" "}
                <strong>drag</strong> on empty areas (not on stimuli).
              </div>
            </div>
            <div className="flex flex-col text-xs">
              <div className="font-semibold">Zooming In/Out</div>
              <div className="flex gap-1">
                Use the <strong>mouse wheel</strong> to zoom in and out.
                Alternatively, you can use the buttons below the view.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};




export interface EditStep {
  arrayType: string;
  editType: 'translate' | 'rotate' | 'mark';
  helpText: string;
}
function getSteps(isStudyEye: boolean): EditStep[] {
  const sharedSteps: EditStep[] = [
    { arrayType: 'BS-C', editType: 'translate',
      helpText: 'Place BS-C in the center of the blindspot.' },
    { arrayType: 'BS-N', editType: 'translate',
      helpText: 'Place the BS-N crossbar at the nasal margin of the blindspot.' },
    { arrayType: 'BS-T', editType: 'translate',
      helpText: 'Place the BS-T crossbar at the temporal margin of the blindspot.' },
  ];
  if (!isStudyEye)
    return [
      ...sharedSteps,
      { arrayType: '', editType: 'mark',
        helpText: 'Mark any stimuli locations which are covered more than 50% by a blood vessel.' },
      { arrayType: 'PG', editType: 'translate',
        helpText: "Move PG stimuli away from scotomas or major blood vessels." },
    ];
  return [
    ...sharedSteps,
    { arrayType: 'SV-1', editType: 'translate',
      helpText: 'Place the SV-1 crossbar along a margin of interest.' },
    { arrayType: 'SH-1', editType: 'translate',
      helpText: 'Place the SH-1 crossbar along a margin of interest near SV-1.' },
    { arrayType: 'SV-2', editType: 'translate',
      helpText: 'Place the SV-2 crossbar along a margin of interest.' },
    { arrayType: 'SH-2', editType: 'translate',
      helpText: 'Place the SH-2 crossbar along a margin of interest near SV-2.' },
    { arrayType: '', editType: 'mark',
      helpText: 'Mark any stimuli locations (both SH/SV and BS) which are covered more than 50% by a blood vessel.' },
    { arrayType: 'HP', editType: 'rotate',
      helpText: "Rotate the HP array until it is away from all other arrays. Avoid placing it near the horizontal axis." },
    { arrayType: 'PG', editType: 'translate',
    helpText: "Move PG stimuli away from scotomas or major blood vessels." },
 ];
}


const fullDeg = octSettings.enfaceAngle;
const scanIntervalDeg = octSettings.patternSize.h / (octSettings.totalBscans - 1);
const scanOffsetDeg = octSettings.totalBscans % 2 == 0 ? (scanIntervalDeg / 2) : 0;

function makeGroup(a: StimulusArray) : StimulusArray[] {
  if (!['T', 'PG'].includes(a.id))
    return [a];
  const result: StimulusArray[] = [];
  for (let i = 0; i < a.members.length; i++) {
    result.push({
      id: a.id,
      members: [a.members[i]],
      coords: [a.coords[i]],
      stimDiameter: a.stimDiameter,
      x: 0,
      y: 0,
    });
  }
  return result;
}

function rowsToArray(arrayID: string, rows: LayoutRow[]) : StimulusArray {
  const coords: Coord[] = rows.map(r => ({ x: r.stimulus_x_deg, y: r.stimulus_y_deg }));
  return {
    id: arrayID,
    members: rows.map(r => r.stimulus_location_id),
    coords: coords,
    stimDiameter: rows[0].stimulus_diameter_deg,
    x: 0,
    y: 0,
  };
}

function renderStaticArray(a: StimulusArray, transform: TransformAction | null) {
  let svgTransform = '';
  if (transform) {
    if (transform.translation)
      svgTransform = `translate(${transform.translation.x}, ${transform.translation.y})`;
    else if (transform.rotation)
      svgTransform = `rotate(${transform.rotation})`;
  }
  const { bounds, center: c, w, h, interval } = getArrayStats(a.coords);
  const labelx = c.x + (h > w ? -1 : 0);
  const labely = c.y + (h > w ? 0 : 1.2);
  let splitter: JSX.Element | undefined = undefined;
  const lineDeg = 2;
  const lineHalf = lineDeg/2;
  if (/-[NTSI12]/.test(a.id)) {
    const lineStart = {
      x: c.x + (h > w ? -lineHalf : 0),
      y: c.y + (h > w ? 0 : -lineHalf),
    };
    const lineEnd = {
      x: c.x + (h > w ? lineHalf : 0),
      y: c.y + (h > w ? 0 : lineHalf),
    };
    splitter = (<line x1={lineStart.x} y1={lineStart.y} x2={lineEnd.x} y2={lineEnd.y}/>);
  }
  let tag = a.id;
  if (['T', 'PG'].includes(a.id))
    tag += ` ${a.members[0]}`;
  return (
    <g key={tag} transform={svgTransform} fill={getArrayColor(a.id)} stroke={getStrokeColor(a.id)} strokeWidth={0.05}>
      {splitter}
      {a.coords.map((c, i) => (
        <circle key={a.members[i]} cx={c.x} cy={c.y} r={a.stimDiameter/2} />
      ))}
      <text fontSize={0.8} textAnchor={h > w ? 'end' : 'middle'} dominantBaseline='middle' strokeWidth={0.02} transform={`translate(${labelx},${labely})`}>{a.id}</text>
      <rect x={bounds.minX-1} y={bounds.minY-1} width={w+2} height={h+2} fill='transparent' strokeWidth={0} />
    </g>
  );
}
